import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

/**
 * Shared site header used on the homepage, FAQ, and any other page that
 * adopts the new design language. Transparent over a colored hero, then
 * transitions to a solid white card once the user scrolls past 60px.
 *
 * The page that uses this header is responsible for placing a colored
 * backdrop behind the top of the document (e.g. the parallax sky or a
 * sky-gradient hero) so the white wordmark stays legible at rest.
 */
const AltHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const aboutRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the About dropdown when the user clicks anywhere outside it
  // (or presses Escape). Without this the menu stays stuck open after
  // the mouse leaves on touch devices.
  useEffect(() => {
    if (!aboutOpen) return undefined;
    const onClick = (e) => {
      if (aboutRef.current && !aboutRef.current.contains(e.target)) {
        setAboutOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setAboutOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [aboutOpen]);

  const ABOUT_ITEMS = [
    { to: '/levanta-story', label: "Levanta's Story" },
    { to: '/mission', label: 'Our Mission' },
    { to: '/vision', label: 'Our Vision' },
    { to: '/founder', label: 'About the Founder' },
    // { to: '/team', label: 'Team' },  // temporarily disabled
  ];

  const NAV_ITEMS = [
    { to: '/how-it-works', label: 'How it works' },
    { to: '/faq', label: 'FAQ' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <header
      /* Mobile: always-opaque white bar for a clear visible separation
         between site header and page hero (cinematic transparent hero was
         designed for desktop where the hero is tall enough for it to
         work). Desktop: keeps the original transparent-then-opaque-on-
         scroll behavior via md: classes. */
      className={`fixed top-0 inset-x-0 z-50 px-6 transition-all duration-300 py-3 bg-white/95 backdrop-blur-md shadow-sm border-b border-black/5 ${
        isScrolled
          ? 'md:py-3 md:bg-white/95 md:backdrop-blur-md md:shadow-sm md:border-black/5'
          : 'md:pt-6 md:pb-2 md:bg-transparent md:backdrop-blur-none md:shadow-none md:border-transparent'
      }`}
      data-testid="alt-header"
      data-scrolled={isScrolled}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Brand wordmark (left) — white over colored hero, blue once scrolled. */}
        <Link
          to="/"
          className="group relative inline-block"
          aria-label="Levanta home"
          data-testid="alt-header-brand"
        >
          <span className="relative block h-7 md:h-9 w-auto">
            <img
              src="/wordmark-blue.png"
              alt="Levanta wordmark"
              aria-hidden="true"
              width="160"
              height="36"
              className="block h-full w-auto select-none"
              draggable={false}
            />
            <img
              src="/wordmark-white.png"
              alt="Levanta"
              draggable={false}
              width="160"
              height="36"
              /* Mobile: header is always opaque white, so the white wordmark
                 must always be hidden (opacity-0) — the blue wordmark
                 underneath shows through. Desktop keeps the original
                 transparent-hero → scrolled swap behavior. */
              className={`absolute inset-0 block h-full w-auto select-none transition-opacity duration-200 opacity-0 ${
                isScrolled
                  ? 'md:opacity-0'
                  : 'md:opacity-100 md:group-hover:opacity-0 md:group-active:opacity-0 md:group-focus-visible:opacity-0'
              }`}
              style={{ filter: 'drop-shadow(0 2px 12px rgba(11, 31, 58, 0.35))' }}
            />
          </span>
        </Link>

        {/* Logo (centered) — same white→blue swap, shrinks once scrolled. */}
        <Link
          to="/"
          className="absolute left-1/2 -translate-x-1/2 group"
          data-testid="alt-header-logo-link"
          aria-label="Levanta home"
        >
          <span
            /* Mobile: compact logo always (matches always-opaque mobile header).
               Desktop keeps the expand-over-hero / shrink-on-scroll effect. */
            className={`relative block transition-all duration-300 h-12 w-12 ${
              isScrolled
                ? 'md:h-12 md:w-12 md:h-14 md:w-14'
                : 'md:h-[67px] md:w-[67px] md:h-[77px] md:w-[77px]'
            }`}
          >
            <img
              src="/logo-blue.png"
              alt="Levanta logo"
              aria-hidden="true"
              width="77"
              height="77"
              className="absolute inset-0 h-full w-full object-contain"
            />
            <img
              src="/logo-white.png"
              alt="Levanta"
              data-testid="alt-header-logo"
              width="77"
              height="77"
              /* Mobile: always hidden (blue logo shows under white opaque bar).
                 Desktop: original transparent-hero → scrolled behavior. */
              className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-200 opacity-0 ${
                isScrolled
                  ? 'md:opacity-0'
                  : 'md:opacity-100 md:group-hover:opacity-0 md:group-active:opacity-0 md:group-focus-visible:opacity-0'
              }`}
            />
          </span>
        </Link>

        {/* Pill nav (right) */}
        <nav
          className={`hidden md:flex items-center gap-1 bg-white rounded-full pl-2 pr-1 py-1 transition-all duration-300 ${
            isScrolled ? 'ring-1 ring-black/5 shadow-none' : 'shadow-sm bg-white/95 backdrop-blur-sm'
          }`}
          aria-label="Primary"
          data-testid="alt-header-nav"
        >
          {/* About dropdown — secondary pages grouped (story, mission,
              vision, founder, team). Opens on hover (desktop) or click /
              keyboard focus; click-outside + Escape close it. */}
          <div
            ref={aboutRef}
            className="relative"
            onMouseEnter={() => setAboutOpen(true)}
            onMouseLeave={() => setAboutOpen(false)}
          >
            <button
              type="button"
              onClick={() => setAboutOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={aboutOpen}
              data-testid="alt-header-about-trigger"
              className="inline-flex items-center gap-1 px-4 py-1.5 text-sm font-semibold text-[#0b1f3a] hover:text-blue-600 transition-colors"
            >
              About
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${aboutOpen ? 'rotate-180' : ''}`}
                strokeWidth={2.5}
              />
            </button>
            {aboutOpen && (
              <div
                role="menu"
                data-testid="alt-header-about-menu"
                className="absolute right-0 top-full pt-2 w-64 z-[60]"
              >
                <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-xl overflow-hidden py-2">
                  {ABOUT_ITEMS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      onClick={() => setAboutOpen(false)}
                      className="block px-5 py-2.5 text-sm font-semibold text-[#0b1f3a] hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="px-4 py-1.5 text-sm font-semibold text-[#0b1f3a] hover:text-blue-600 transition-colors"
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/download-app"
            data-testid="alt-header-cta"
            className="ml-1 px-4 py-1.5 rounded-full bg-[#0b1f3a] text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
          >
            Download
          </Link>
        </nav>

        {/* Mobile menu trigger */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen((v) => !v)}
          className={`md:hidden p-2 rounded-full text-[#0b1f3a] transition-all duration-300 ${
            isScrolled ? 'bg-white ring-1 ring-black/5' : 'bg-white/95'
          }`}
          data-testid="alt-header-mobile-toggle"
          aria-expanded={mobileMenuOpen}
          aria-label="Toggle navigation"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <nav
          className="md:hidden mt-4 mx-auto max-w-md rounded-2xl bg-white/95 backdrop-blur-sm shadow-lg p-2"
          aria-label="Mobile primary"
          data-testid="alt-header-mobile-nav"
        >
          {/* Download — first item, full-blue button so it visually leads
              the menu. Always reachable from any page on mobile. */}
          <Link
            to="/download-app"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="alt-header-mobile-download"
            className="block mb-2 px-4 py-3 text-center text-white font-bold rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 shadow-md active:scale-[0.98] transition-transform"
          >
            Download the app
          </Link>

          {/* Collapsible About group — taps expand in place so the list
              stays inside the drawer instead of pushing users to a
              separate screen. */}
          <button
            type="button"
            onClick={() => setMobileAboutOpen((v) => !v)}
            aria-expanded={mobileAboutOpen}
            data-testid="alt-header-mobile-about-toggle"
            className="w-full flex items-center justify-between px-4 py-3 text-[#0b1f3a] font-semibold rounded-xl hover:bg-blue-50"
          >
            About
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`}
              strokeWidth={2.5}
            />
          </button>
          {mobileAboutOpen &&
            ABOUT_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => {
                  setMobileMenuOpen(false);
                  setMobileAboutOpen(false);
                }}
                className="block pl-8 pr-4 py-2.5 text-sm text-[#0b1f3a]/80 font-medium rounded-xl hover:bg-blue-50 hover:text-blue-600"
              >
                {item.label}
              </Link>
            ))}

          {/* Remaining primary items — Download is now at the top so
              it is intentionally excluded from this loop. */}
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 text-[#0b1f3a] font-semibold rounded-xl hover:bg-blue-50"
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};

export default AltHeader;
